import {
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import { cn } from './cn';

/*
  Table — коллекция однородного для сравнения. Липкая шапка, строки по плотности режима
  (dense — 32 px для пульта), числа справа табличными цифрами, действия строки — по наведению
  (`group-hover/row`), выбранная строка — bg-selected.
*/

interface TableProps extends HTMLAttributes<HTMLTableElement> {
  dense?: boolean;
  /** Opt-in spacing for a title and subtitle; one-line rows retain their default. */
  rowLines?: 1 | 2;
  /** Необязательный собственный viewport длинной таблицы; прежний wrapper по умолчанию. */
  viewportClassName?: string;
  viewportRef?: Ref<HTMLDivElement>;
  /** Подгрузка внутри той же прокрутки, сразу под строками. */
  footer?: ReactNode;
  /** Только для широкой таблицы: закрепить первую колонку. */
  stickyFirst?: boolean;
  /** Доступность и события собственной прокрутки; без свойства прежний wrapper. */
  viewportProps?: HTMLAttributes<HTMLDivElement>;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      dense,
      rowLines = 1,
      viewportClassName,
      viewportRef,
      footer,
      stickyFirst,
      viewportProps,
      ...rest
    },
    ref,
  ) => (
    <div
      {...viewportProps}
      ref={viewportRef}
      data-scrollport={viewportClassName ? '' : undefined}
      className={cn('w-full overflow-x-auto', viewportClassName)}
    >
      <table
        ref={ref}
        data-slot="table"
        data-sticky-first={stickyFirst ? '' : undefined}
        data-dense={dense ? '' : undefined}
        data-row-lines={rowLines === 2 ? 2 : undefined}
        className={cn(
          'w-full border-collapse text-left text-sm',
          rowLines === 2 ? '[&_td]:h-14 [&_td]:py-2' : dense ? '[&_td]:h-8' : '[&_td]:h-10',
          className,
        )}
        {...rest}
      />
      {footer}
    </div>
  ),
);
Table.displayName = 'Table';

export const TableHeader = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...rest }, ref) => (
    <thead ref={ref} className={cn('sticky top-0 z-raised bg-card', className)} {...rest} />
  ),
);
TableHeader.displayName = 'TableHeader';

export const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...rest }, ref) => <tbody ref={ref} className={cn(className)} {...rest} />,
);
TableBody.displayName = 'TableBody';

export const TableFooter = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...rest }, ref) => (
    <tfoot
      ref={ref}
      className={cn('sticky bottom-0 z-raised border-t border-line bg-card', className)}
      {...rest}
    />
  ),
);
TableFooter.displayName = 'TableFooter';

interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
  interactive?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, selected, interactive, ...rest }, ref) => (
    <tr
      ref={ref}
      data-state={selected ? 'selected' : undefined}
      className={cn(
        'group/row border-b border-line transition-colors duration-fast last:border-b-0',
        interactive && 'cursor-pointer hover:bg-hover',
        selected && 'bg-selected',
        className,
      )}
      {...rest}
    />
  ),
);
TableRow.displayName = 'TableRow';

interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
}

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, numeric, ...rest }, ref) => (
    <th
      ref={ref}
      className={cn(
        'h-9 border-b border-line px-3 align-middle text-2xs font-semibold tracking-wider text-fg-subtle uppercase',
        numeric && 'text-right',
        className,
      )}
      {...rest}
    />
  ),
);
TableHead.displayName = 'TableHead';

interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
  muted?: boolean;
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, numeric, muted, ...rest }, ref) => (
    <td
      ref={ref}
      className={cn(
        'px-3 align-middle',
        numeric && 'text-right font-mono text-xs tabular-nums',
        muted ? 'text-fg-muted' : 'text-fg',
        className,
      )}
      {...rest}
    />
  ),
);
TableCell.displayName = 'TableCell';
